import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Video, MessageSquarePlus, Trash2, Save, Plus } from "lucide-react";

const MAX_QA = 10;

export default function CustomTraining() {
  const [videoUrl, setVideoUrl] = useState("");
  const [qaItems, setQaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leader/training");
        setVideoUrl(data.welcome_video_url || "");
        setQaItems(data.qa_items || []);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addQA = () => {
    if (qaItems.length >= MAX_QA) return;
    setQaItems([...qaItems, { question: "", answer: "" }]);
  };

  const updateQA = (idx, field, value) => {
    const next = [...qaItems];
    next[idx] = { ...next[idx], [field]: value };
    setQaItems(next);
  };

  const removeQA = (idx) => {
    setQaItems(qaItems.filter((_, i) => i !== idx));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/leader/training", {
        welcome_video_url: videoUrl,
        qa_items: qaItems,
      });
      setQaItems(data.qa_items);
      toast.success("Training content saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="glass-strong rounded-sm overflow-hidden" data-testid="custom-training-section">
      <div className="px-6 py-4 flex items-center justify-between border-b border-white/10">
        <span className="hud-label">Custom Training · Briefing Kit</span>
        <button
          onClick={saveAll}
          disabled={saving}
          data-testid="training-save-button"
          className="btn-gold px-4 py-2 rounded-sm text-xs flex items-center gap-2 disabled:opacity-60"
        >
          <Save size={12} />
          {saving ? "Saving..." : "Save All"}
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-slate-500 text-sm text-center">Loading training kit...</div>
      ) : (
        <div className="p-6 space-y-8">
          {/* Welcome Video */}
          <div>
            <label className="block">
              <div className="flex items-center gap-2 mb-3">
                <Video size={14} className="text-gold" />
                <span className="hud-label text-[10px]">Custom Welcome Video for Your Recruits</span>
              </div>
              <input
                type="url"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://your-cdn.com/welcome.mp4"
                data-testid="training-video-url-input"
                className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-gold transition-colors"
              />
            </label>
            {videoUrl && (
              <div className="mt-3 text-xs text-slate-500 font-mono-aviation truncate" data-testid="training-video-current">
                Current: <span className="text-gold">{videoUrl}</span>
              </div>
            )}
          </div>

          {/* Q&A */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageSquarePlus size={14} className="text-gold" />
                <span className="hud-label text-[10px]">Team Q&amp;A (shown to your recruits on login)</span>
              </div>
              <span className="text-[10px] text-slate-500 font-mono-aviation" data-testid="training-qa-count">
                {qaItems.length} / {MAX_QA}
              </span>
            </div>

            <div className="space-y-3" data-testid="training-qa-list">
              {qaItems.length === 0 && (
                <div className="text-xs text-slate-500 italic p-4 border border-dashed border-white/10 rounded-sm text-center">
                  No Q&amp;A yet. Add your first briefing answer below.
                </div>
              )}
              {qaItems.map((qa, idx) => (
                <div
                  key={idx}
                  className="border border-white/10 rounded-sm p-4 space-y-2 hover:border-gold/40 transition-colors"
                  data-testid={`training-qa-item-${idx}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="font-mono-aviation text-[10px] text-gold mt-2 w-6 shrink-0">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={qa.question}
                        onChange={(e) => updateQA(idx, "question", e.target.value)}
                        placeholder="Question"
                        data-testid={`training-qa-question-${idx}`}
                        className="w-full bg-[#060B14] border border-white/10 rounded-sm px-3 py-2 text-white text-sm focus:outline-none focus:border-gold"
                      />
                      <textarea
                        value={qa.answer}
                        onChange={(e) => updateQA(idx, "answer", e.target.value)}
                        placeholder="Answer"
                        rows={3}
                        data-testid={`training-qa-answer-${idx}`}
                        className="w-full bg-[#060B14] border border-white/10 rounded-sm px-3 py-2 text-white text-sm focus:outline-none focus:border-gold resize-none"
                      />
                    </div>
                    <button
                      onClick={() => removeQA(idx)}
                      data-testid={`training-qa-remove-${idx}`}
                      className="text-slate-500 hover:text-[#F43F5E] p-2 transition-colors"
                      aria-label="Remove Q&A"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addQA}
              disabled={qaItems.length >= MAX_QA}
              data-testid="training-add-qa-button"
              className="btn-ghost-gold mt-4 px-4 py-2 rounded-sm text-xs flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus size={12} />
              {qaItems.length >= MAX_QA ? "Maximum reached" : "Add Q&A"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
